/**
 * Public release notes — chronological by month, customer-facing.
 *
 * One entry per month. Each release item is a short bullet describing
 * what the feature *does for customers*, not how it was built. Skip
 * anything invisible (refactors, build fixes, internal performance,
 * tech debt).
 *
 * Adding a release: edit the RELEASES array. `month` is YYYY-MM; items
 * are 1-2 sentence summaries listed newest-first within the month.
 */
import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { AppHeader } from '../components/AppHeader';
import { useDocumentTitle } from '../lib/use-document-title';

export const Route = createFileRoute('/release-notes')({
  component: ReleaseNotesPage,
});

interface ReleaseItem {
  title: string;
  body: string;
}

interface MonthlyRelease {
  /** YYYY-MM. Used for display + sort order. */
  month: string;
  summary?: string;
  items: ReleaseItem[];
}

const RELEASES: MonthlyRelease[] = [
  {
    month: '2026-05',
    summary:
      'The first public month. Bookings, guided chat and the eleven-language booking page went live; then the voice assistant, business hours, address autocomplete and team controls; then booking approvals, the waiting list and editable email/SMS templates — with a new rule that every paid feature is visible (just disabled) on every plan. Closed the month with a heavy voice-polish pass and chat translations on every plan.',
    items: [
      {
        title: 'Finish-setting-up card gains three new reminders',
        body:
          "The setup checklist on /admin/<your-slug> grows beyond hours / address / services / team. New rows: 'Block your public holidays' (done once at least one closed day is set for today-or-later; past entries don't count), 'Set your service area' (only shown for tenants with on-site services; nags until you list the postcodes you cover), and 'Every service requirement is fillable' (flags services whose required skills no one on the team has, or required features no resource has — the allocator can't book them). Plus 'Translate every service' for tenants who've shown i18n intent — a non-English default locale, or any translation already saved. Each row's help text lists up to three affected services with a count, e.g. 'Bowen therapy (3), Acupuncture (1), and 2 more'. English-only tenants and tenants without on-site services don't see the rows that don't apply to them.",
      },
      {
        title: 'Translations flag themselves stale when you rename a service',
        body:
          "When you rename a service in English, the existing translations don't auto-update — they keep saying the old thing in every language. Translations now record the English source value at write-time. Any time you rename the service afterwards, the per-locale tiles on /admin/<slug>/translations light up with an amber 'Stale' badge so you know which ones need re-translating. Hover the badge to see the old vs new English. Existing translations from before this change show no flag (we have no signal on them) — they'll start flagging once you next save them.",
      },
      {
        title: 'Duplicate a service in one click',
        body:
          "Services now carry a lot of config — duration, location, business-hours overrides, who can book it, staff skill requirements, room and feature requirements, the staff allowlist. Re-entering all of that for a near-identical service is tedious. A new Duplicate button on each row on /admin/<slug>/services copies all of that into a new active service named '<original> (copy)' that's ready to rename and tweak. Translations aren't copied — add them on the Translations page if needed.",
      },
      {
        title: 'Booking page refuses to take bookings when hours aren’t set',
        body:
          'A new business that hadn’t yet published its opening hours was still accepting bookings — clients could pick a Sunday for a Mon–Fri business and get confused later. Now the public booking page shows a "bookings paused" banner and hides itself when hours are unconfigured; the booking POST, voice and chat APIs all refuse with a clear error; and admins get a top-of-page reminder linking straight to Settings → Business hours. Brand-new businesses default to Mon–Fri 9–5 on creation so this state never appears unless someone explicitly clears their hours.',
      },
      {
        title: 'See who cancels and who no-shows',
        body:
          "Every cancellation now records who actually clicked cancel — the client or someone on your team — so a staff sick-day doesn't make a customer look unreliable. Past confirmed bookings get a 'Mark no-show' action from the calendar. On each booking pill, a small icon flags the client's last interaction: red for a no-show, amber for a self-cancel, sage for one your team cancelled. The same indicator surfaces on My Day so staff see it before the appointment.",
      },
      {
        title: 'Clients page: cancellation + no-show counts at a glance',
        body:
          "A new Clients page (/admin/<your-slug>/clients) lists every client this business has on file with their cancellation and no-show counts over the last 30, 90, 180 and 365 days. Repeat offenders sort to the top. Client cancellations and team-side cancellations are tracked in separate columns so a staff sick-day doesn't taint a customer's history.",
      },
      {
        title: 'Per-service client eligibility (new / returning / any)',
        body:
          "Mark a service as open to anyone (default), first-time clients only (intro offers, new-patient consults), or existing clients only (loyalty follow-ups). The booking page surfaces it so clients understand before they pick; the booking POST enforces it at the gate. Admin-created bookings bypass — you can always book on a client's behalf.",
      },
      {
        title: 'Sign in to link past bookings to your account',
        body:
          "Open a /m/<token> magic-link, then sign in or sign up — your existing booking now rolls up under your account, and any email/phone the booking had backfills onto your client record. Subsequent bookings under the same email automatically link to the same identity. The magic link still works for cancellations / reschedules afterwards; it's not consumed by signing in.",
      },
      {
        title: 'Voice assistant reads back the service you picked',
        body:
          'After you pick a service by voice, the assistant now says "Booking X-ray. Is that right?" and waits for yes or no. Service names get misheard surprisingly often (try "hedge trimming" in a noisy room) and a one-second yes/no beats a wrong booking. Tapping a service button on screen skips the read-back — you obviously meant the one you tapped.',
      },
      {
        title: '"Say again" / "repeat" works at any point',
        body:
          'Missed what the assistant just said? Say "repeat", "say again", "one more time" or "what was that" and the last prompt is re-read. The service list specifically ends with "to hear these again, say repeat" because tenant-defined service names are the hardest to catch first time around.',
      },
      {
        title: 'End a call by saying "goodbye" — or just go silent',
        body:
          'Hang up by saying "bye", "goodbye", "end call", "hang up", "thanks", or similar from any point. If you fall silent for thirty seconds the assistant checks in: "Are you still on the line?" Another thirty seconds of silence and it hangs up gracefully. The voice page tells you the call ended due to inactivity so it’s never mysterious.',
      },
      {
        title: 'Voice picks up "create a booking" / "reschedule" / "book me in"',
        body:
          'A much wider set of natural phrasings now route to the right intent. "Modify booking" matches "Modify a booking" (callers drop articles all the time). "Create a booking", "book me in", "reschedule", "change my appointment", "remove booking" all land on the right action without the assistant getting confused.',
      },
      {
        title: 'Day picker only offers days that actually have openings',
        body:
          'Picking a day, getting told "no times available", picking another, repeat. Gone. The voice and chat assistants now scan up to fourteen days ahead and only offer days that have bookable slots. Closed days, fully-booked days, holiday days — never shown.',
      },
      {
        title: 'Full day of times shown',
        body:
          'The time picker used to cap at twelve options because reading a long list out by voice was unmanageable. Now the screen shows the entire day; the voice assistant says "Tap a time, or say one like ten thirty or two pm" instead of trying to recite thirty options.',
      },
      {
        title: 'Voice narrows long time lists by morning or afternoon',
        body:
          'When a service has more than four openings on the chosen day and both halves of the day are available, the voice assistant asks "Would you prefer morning or afternoon?" first, then shows the filtered list. Aliases like "AM", "before midday", "evening" and "after lunch" all map cleanly.',
      },
      {
        title: 'Voice booking-link survives a failed SMS',
        body:
          'After a voice call you get a text with the booking link. If the SMS doesn’t go through (carrier filter, bad number, network hiccup), the booking link now also appears as a tappable button on the voice page itself. The assistant is explicit that your booking is not confirmed yet — you still have to open the booking page and complete the form.',
      },
      {
        title: '"Say option 1 for X-ray, option 2 for MRI…"',
        body:
          'When listing services the assistant now says "say option 1 for X" instead of "say 1 for X". The extra word gives speech recognition more context, so callers saying "two" or "four" get understood instead of being mis-transcribed as "to" or "before".',
      },
      {
        title: 'Booking-assistant chat speaks 11 languages',
        body:
          'The booking-assistant chat (your /chat link) now speaks English (Australia) plus the ten most-spoken non-English languages in Australia per the ABS 2021 census: Mandarin, Arabic, Vietnamese, Cantonese, Punjabi, Greek, Italian, Hindi, Spanish, Tagalog. There’s a globe-icon switcher in the header, and translated service names use your existing admin translations. Available on every plan, including Free — accessibility isn’t a premium feature.',
      },
      {
        title: 'Help button on the voice page',
        body:
          'The Help button on the voice page now answers the obvious questions: what is this, can I ring a phone number, does it actually create the booking, how do I end the call, what if it mishears me, which languages does it support.',
      },
      {
        title: 'Design notes published',
        body:
          'A few of the trickier voice/chat decisions are now public on the marketing site at bookaburra.com/adrs — how we route caller intent without an LLM on the common path, why the voice assistant never auto-creates a booking, and how we keep call costs predictable. Useful if you’re curious about the thinking behind the product.',
      },
      {
        title: 'Require booking approval before a booking is confirmed',
        body:
          'Some bookings need a second pair of eyes. Flip "Require booking approval" on Settings and new bookings hold the slot but stay pending until you decide. You get an email with a magic-link to approve or reject — no sign-in needed — with an optional message that goes straight to the customer. There’s also a dedicated Approvals page on every admin, sorted soonest-first with red badges for today and amber for the next open day.',
      },
      {
        title: 'Waiting list when slots run out',
        body:
          'When a service has no times available on the day a client wants, they can join a waiting list for that day. If anyone cancels — or you reshuffle to free a slot — everyone on the list gets emailed simultaneously and races to book the spot. You can see the list on a dedicated Waiting list page and remove entries on a client’s behalf. Small business and Large business plans only.',
      },
      {
        title: 'Customise email + SMS wording',
        body:
          'Edit every booking confirmation, reminder, cancellation, approval notice and waiting-list email or SMS your customers receive. Insert variables with one click ({{client_name}}, {{when}}, {{manage_url}}…), see a live preview, and watch a character-budget indicator warn before an SMS would split into multiple segments. Large business only — other plans use the Bookaburra defaults.',
      },
      {
        title: 'Every paid feature visible to every plan',
        body:
          'New rule across the admin: features you don’t have on your current plan are no longer hidden. You’ll see the page, the controls are disabled, and a small banner explains which plan unlocks it. Nothing to upgrade-blind shop for anymore — what you’d get on Small business or Large business is right there.',
      },
      {
        title: '"Manage your booking" link in every email',
        body:
          'Every customer email now carries a single magic-link to the booking management page. One link, every booking state — pending approval, confirmed, rescheduled, cancelled, waiting-listed — without logging in.',
      },
      {
        title: 'Talk to your business by voice',
        body:
          'Booking sites on Small and Large plans now show a "Call our assistant" link. Clients tap it, speak naturally ("make a booking, an X-ray, Thursday, ten am"), and the assistant walks through service, day, and time. The phone number is read back in 04xx form for confirmation, then we text them the booking link to confirm — no email-spelling required.',
      },
      {
        title: 'Free trial sign-up button refreshed across the product',
        body:
          'Every primary call-to-action — Start free trial, Start call, Open booking page, every form submit — is back to the lighter sage-green style with a subtle shadow. The chunky high-contrast version we briefly used after the marketing/app button consolidation is gone.',
      },
      {
        title: 'Already signed in? We send you to the app, not the form',
        body:
          'Visiting /signin or /signup while you have an active session now redirects to wherever you were going (or the app home), instead of showing an empty form you don’t need.',
      },
      {
        title: 'Invite teammates by email, even if they don’t have an account',
        body:
          'Add a colleague by email from the Team page. If they’re already on Bookaburra they’re added on the spot; if not, they get a branded email with a sign-up link that drops them straight into your workspace once they finish signing up.',
      },
      {
        title: 'Business address with autocomplete',
        body:
          'Settings now has a business address field with live Australian address search. Pick a result and the full street, suburb, state and postcode are saved — no half-addresses possible.',
      },
      {
        title: 'Weekly business hours',
        body:
          'Set open / close times for each day of the week from Settings. Mark days off as Closed. Single-day closures (public holidays, owner away) still go on the Closed days page.',
      },
      {
        title: '"At our place" labels show your address',
        body:
          'Service badges everywhere — booking page, admin services list, service editor — now read "At 9 Help St, Chatswood" instead of the generic "At our place" once you’ve set a business address.',
      },
      {
        title: 'Service skills, rooms and team composition',
        body:
          'A service can now declare exactly who’s needed: skills required on each person, how many people, and which rooms or equipment. The booking system only offers slots where everything’s available. Edit it all from the service’s Add / Edit dialog.',
      },
      {
        title: 'Soft delete for skills, features and rooms',
        body:
          'Removing a skill, feature, or room archives it instead of erasing the record. Past bookings keep their audit trail; archived items can be restored anytime from Capabilities.',
      },
      {
        title: 'Calendar shows the language each client booked in',
        body:
          'Every booking pill on the staff calendar carries a small badge when the client made the booking in a non-default language (e.g. Punjabi, Mandarin) — so staff know if a translator may be helpful.',
      },
      {
        title: 'Per-day staff strip above each calendar day',
        body:
          'The three-day staff calendar now lists who’s working at the top of each day, colour-coded for sick / off / extra cover. Replaces the separate "X staff overrides today" alert.',
      },
      {
        title: 'Owners can’t accidentally remove themselves',
        body:
          'Self-removing as the owner would lock you out of your own workspace. Now blocked with a clear message — promote a co-owner first if you really need to leave.',
      },
      {
        title: 'Calendar + time picker selection now obvious on mobile',
        body:
          'Selected dates and times use a bold green highlight with a ring outline, so a tap is unmistakable on a phone in daylight.',
      },
      {
        title: 'Demo calendars on the marketing site',
        body:
          'bookaburra.com/demos now shows what the staff calendar looks like for each of the three sample businesses — live rendering with illustrative bookings.',
      },
      {
        title: 'Book online without an account',
        body:
          'Clients pick a service, choose a time, and get a confirmation by email — no signup, no app to download.',
      },
      {
        title: 'Manage bookings via magic link',
        body:
          'Every confirmation email and reminder carries a personal link clients can use to reschedule, cancel, or view their booking without creating an account.',
      },
      {
        title: 'Automatic 24-hour and 1-hour reminders',
        body:
          'Email reminders go out the day before and an hour before each booking. The link in each one lets the client reschedule on the spot.',
      },
      {
        title: 'Eleven languages on the booking page',
        body:
          'English plus Mandarin, Arabic, Vietnamese, Cantonese, Punjabi, Greek, Italian, Hindi, Spanish, and Filipino — the ten most-spoken non-English languages in Australia.',
      },
      {
        title: 'Booking assistant chat',
        body:
          'A guided chat walks clients through "book a service / change a booking / cancel" with one tap per choice — useful when they’re not sure what they need.',
      },
      {
        title: 'Phone and virtual services',
        body:
          'Services can be marked as "at our place", "at your place", "virtual", or "phone call" so clients see exactly where (or how) the appointment happens.',
      },
      {
        title: 'Public holidays and closed days',
        body:
          'Block out dates in admin and the booking page automatically greys them out — no chance of accidental Anzac Day bookings.',
      },
      {
        title: 'Day-at-a-glance staff calendar',
        body:
          'The admin home shows today and the next two days side-by-side, with every booking on the right person’s schedule.',
      },
      {
        title: 'Sick-day reshuffle',
        body:
          'Mark a staff member unavailable for a window and Bookaburra proposes a new time for each affected booking. You approve, the clients get re-confirmed by email.',
      },
      {
        title: 'Service editor — skills, rooms, and team shape',
        body:
          'Each service says which skills its staff need, how many people are required, and what rooms or equipment are involved. The booking system only offers times when everything’s actually available.',
      },
      {
        title: 'Team members and skills',
        body:
          'Invite staff by email, set their role (owner / admin / staff), and tick the credentials they hold. Services automatically only show up when a qualified person is free.',
      },
      {
        title: 'Multi-language service names',
        body:
          'Admins can translate service names per locale — assisted by an in-browser AI suggestion you can edit before saving — so the booking page reads naturally in every supported language.',
      },
      {
        title: 'Workspace switcher',
        body:
          'Staff who work across more than one Bookaburra business get a dropdown in the top right — one click to jump between admin views.',
      },
      {
        title: 'Archive instead of delete',
        body:
          'Services, skills, rooms, and equipment can be archived — they disappear from new bookings but stay in your history so past appointments still read correctly. Restore any time.',
      },
      {
        title: 'Demos and screenshots on the marketing site',
        body:
          'Three live demo businesses, plus a behind-the-scenes preview of the staff calendar, so you can see exactly what you’re signing up for.',
      },
    ],
  },
];

function ReleaseNotesPage() {
  useDocumentTitle('Release notes');

  // Newest month first.
  const ordered = [...RELEASES].sort((a, b) => b.month.localeCompare(a.month));

  return (
    <div className="min-h-dvh bg-background">
      <AppHeader />
      <main className="mx-auto max-w-3xl px-6 py-12 sm:px-12 sm:py-16">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Home
        </Link>
        <header className="mt-4">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">
            Release notes
          </p>
          <h1 className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">
            What&rsquo;s new
          </h1>
          <p className="mt-4 text-base text-muted-foreground">
            A monthly log of what changed for you and your clients. Quiet
            months happen — we ship features only when they&rsquo;re ready.
          </p>
        </header>

        <ol className="mt-12 space-y-14">
          {ordered.map((release) => (
            <li key={release.month}>
              <header className="mb-5 border-b border-brand-sage/15 pb-3">
                <h2 className="text-xl font-bold tracking-tight">
                  {formatMonth(release.month)}
                </h2>
                {release.summary ? (
                  <p className="mt-1 text-sm text-muted-foreground">
                    {release.summary}
                  </p>
                ) : null}
              </header>
              <ul className="space-y-5">
                {release.items.map((item, i) => (
                  <li key={i}>
                    <h3 className="text-sm font-semibold text-foreground">
                      {item.title}
                    </h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      {item.body}
                    </p>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>

        <p className="mt-16 border-t border-brand-sage/15 pt-6 text-xs text-muted-foreground">
          Got a question about something here?{' '}
          <a
            className="text-primary hover:underline"
            href="https://bookaburra.com/contact"
          >
            Get in touch
          </a>
          .
        </p>
      </main>
    </div>
  );
}

function formatMonth(yyyymm: string): string {
  const [y, m] = yyyymm.split('-').map(Number);
  if (!y || !m) return yyyymm;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-AU', {
    timeZone: 'UTC',
    month: 'long',
    year: 'numeric',
  });
}
